import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { trainingReportApi } from '../../services/trainingReportApi';
import { useData } from './DataContext';
import { TrainingReportAttendanceStep } from './TrainingReportAttendanceStep';
import { TrainingReportEvidenceStep } from './TrainingReportEvidenceStep';
import { TrainingReportGeneralStep } from './TrainingReportGeneralStep';
import { TrainingReportGenerateStep } from './TrainingReportGenerateStep';
import { TrainingReportPreviewStep } from './TrainingReportPreviewStep';
import { TrainingReportResultsStep } from './TrainingReportResultsStep';
import { TrainingReportReviewStep } from './TrainingReportReviewStep';
import './AdminOperations.css';
import './TrainingReports.css';

const STEPS = [
  'Datos generales',
  'Participantes y asistencia',
  'Resultados',
  'Evidencias',
  'Revisión',
  'Vista previa',
  'Generar',
];

export const TrainingReportEditor = () => {
  const { idReporte } = useParams();
  const navigate = useNavigate();
  const { data } = useData();
  const accessToken = data?.access_token;
  const [report, setReport] = useState(null);
  const [activeStep, setActiveStep] = useState(0);
  const [maxUnlockedStep, setMaxUnlockedStep] = useState(4);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    let active = true;

    const loadReport = async () => {
      if (!accessToken) {
        setError(
          'La sesión administrativa no está disponible. Inicia sesión nuevamente.',
        );
        setLoading(false);
        return;
      }

      try {
        const result = await trainingReportApi.get(idReporte, accessToken);
        if (active) {
          setReport(result);
          if (result?.estado === 'generado') {
            setMaxUnlockedStep(6);
          }
        }
      } catch (requestError) {
        console.error('No se pudo cargar el reporte:', requestError);
        if (active) {
          setError(
            requestError?.message || 'No fue posible cargar el reporte.',
          );
        }
      } finally {
        if (active) setLoading(false);
      }
    };

    loadReport();
    return () => {
      active = false;
    };
  }, [accessToken, idReporte]);

  const handleReportChange = (nextReport) => {
    setReport(nextReport);
    if (activeStep < 4) {
      setMaxUnlockedStep(4);
    }
  };

  const goToStep = (index) => {
    if (index <= maxUnlockedStep) setActiveStep(index);
  };

  const continueToPreview = () => {
    setMaxUnlockedStep((current) => Math.max(current, 5));
    setActiveStep(5);
  };

  const continueToGenerate = () => {
    setMaxUnlockedStep((current) => Math.max(current, 6));
    setActiveStep(6);
  };

  const renderStep = () => {
    if (activeStep === 0) {
      return (
        <TrainingReportGeneralStep
          idReporte={idReporte}
          accessToken={accessToken}
          report={report}
          setReport={handleReportChange}
          onBackToList={() => navigate('/a-reportes-capacitacion')}
          onNext={() => setActiveStep(1)}
        />
      );
    }

    if (activeStep === 1) {
      return (
        <TrainingReportAttendanceStep
          idReporte={idReporte}
          accessToken={accessToken}
          report={report}
          setReport={handleReportChange}
          onPrevious={() => setActiveStep(0)}
        />
      );
    }

    if (activeStep === 2) {
      return (
        <TrainingReportResultsStep
          idReporte={idReporte}
          accessToken={accessToken}
          report={report}
          setReport={handleReportChange}
          onPrevious={() => setActiveStep(1)}
          onNext={() => setActiveStep(3)}
        />
      );
    }

    if (activeStep === 3) {
      return (
        <>
          <TrainingReportEvidenceStep
            idReporte={idReporte}
            accessToken={accessToken}
            report={report}
            setReport={handleReportChange}
            onPrevious={() => setActiveStep(2)}
          />
          <div className="training-report-editor-actions">
            <button
              className="admin-operation-button"
              type="button"
              onClick={() => setActiveStep(4)}
            >
              Ir a revisión
            </button>
          </div>
        </>
      );
    }

    if (activeStep === 4) {
      return (
        <TrainingReportReviewStep
          idReporte={idReporte}
          accessToken={accessToken}
          report={report}
          setReport={handleReportChange}
          onPrevious={() => setActiveStep(3)}
          onNext={continueToPreview}
        />
      );
    }

    if (activeStep === 5) {
      return (
        <TrainingReportPreviewStep
          idReporte={idReporte}
          accessToken={accessToken}
          report={report}
          onPrevious={() => setActiveStep(4)}
          onNext={continueToGenerate}
        />
      );
    }

    return (
      <TrainingReportGenerateStep
        idReporte={idReporte}
        accessToken={accessToken}
        report={report}
        setReport={handleReportChange}
        onPrevious={() => setActiveStep(5)}
        onBackToList={() => navigate('/a-reportes-capacitacion')}
      />
    );
  };

  return (
    <div className="admin-operation-page training-reports-page">
      <main className="admin-operation-container">
        <header className="admin-operation-header">
          <p className="admin-operation-eyebrow">Reporte por acción de capacitación</p>
          <h1>{report?.nombre_accion || 'Editor del reporte'}</h1>
          <p>
            {report
              ? `${report.matricula_clase || ''} · ${report.nombre_instructor || 'Instructor pendiente'}`
              : 'Cargando información del borrador...'}
          </p>
        </header>

        <section className="admin-operation-panel">
          <div className="training-report-stepper" aria-label="Etapas del reporte">
            {STEPS.map((step, index) => {
              const enabled = index <= maxUnlockedStep;
              return (
                <button
                  className={`training-report-step ${index === activeStep ? 'training-report-step-active' : ''}`}
                  key={step}
                  type="button"
                  onClick={() => goToStep(index)}
                  disabled={!enabled}
                  aria-current={index === activeStep ? 'step' : undefined}
                  title={enabled ? `Ir a ${step}` : 'Completa la etapa anterior para continuar'}
                >
                  <span>{index + 1}</span>
                  <strong>{step}</strong>
                </button>
              );
            })}
          </div>
        </section>

        <section className="admin-operation-panel">
          {loading ? (
            <p className="admin-operation-empty">Cargando reporte...</p>
          ) : error ? (
            <div className="admin-operation-status admin-operation-status-error" role="status">
              {error}
            </div>
          ) : (
            renderStep()
          )}
        </section>
      </main>
    </div>
  );
};
